import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { useCreateCongaTestBatchMutation, useListCongaTestBatchesQuery } from '@/services/congaTestApi';
import { PalmettoButton } from '@palmetto/dsm-components';
import { type Column, Table, toast } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { CONGA_FINANCE_TYPES, CONGA_PROGRAM_TYPES, type CongaTestBatchSummary } from 'types';

import TEMPLATE_CSV from './conga-test-envelopes-template.csv?raw';

const TEMPLATE_FILE_NAME = 'conga-test-envelopes-template.csv';

const getApiMessage = (err: unknown): string | undefined =>
  err && typeof err === 'object' && 'data' in err
    ? (err as { data?: { message?: string } }).data?.message
    : undefined;

const formatStartedAt = (value: string): string => DateTime.fromISO(value).toFormat('M/d/yy h:mm a');

const formatRunStatus = ({ rowCount, succeededCount, failedCount }: CongaTestBatchSummary): string => {
  const inProgress = rowCount - succeededCount - failedCount;
  return [
    succeededCount > 0 && `${succeededCount} succeeded`,
    failedCount > 0 && `${failedCount} failed`,
    inProgress > 0 && `${inProgress} in progress`,
  ]
    .filter(Boolean)
    .join(' · ');
};

const SCROLL_X = { x: true };
const NO_BATCHES: CongaTestBatchSummary[] = [];

const PREVIOUS_RUN_COLUMNS: Column[] = [
  {
    heading: '',
    dataKey: 'batchId',
    render: (_cell, row) => (
      <Link to={`/internal/conga-test/${(row as CongaTestBatchSummary).batchId}`} className="text-text-link">
        View batch
      </Link>
    ),
  },
  { heading: 'Ran by', dataKey: 'createdByName' },
  {
    heading: 'Started',
    dataKey: 'createdAt',
    render: (_cell, row) => formatStartedAt((row as CongaTestBatchSummary).createdAt),
  },
  { heading: 'Rows', dataKey: 'rowCount' },
  {
    heading: 'Status',
    dataKey: 'status',
    render: (_cell, row) => formatRunStatus(row as CongaTestBatchSummary),
  },
];

const PreviousRuns = () => {
  // Refetch on mount so a run started earlier shows up when the user comes back to this page.
  const { data, isLoading, isError } = useListCongaTestBatchesQuery(undefined, { refetchOnMountOrArgChange: true });
  const batches = data?.batches ?? NO_BATCHES;

  let content: React.ReactNode;
  if (isError) {
    content = <p className="text-sm text-text-secondary">Could not load previous runs.</p>;
  } else if (!isLoading && batches.length === 0) {
    content = <p className="text-sm text-text-secondary">No previous runs yet.</p>;
  } else {
    content = (
      <Table
        isLoading={isLoading}
        rowKey="batchId"
        columns={PREVIOUS_RUN_COLUMNS}
        rows={batches}
        isScrollable={SCROLL_X}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <span className="font-bold text-text-primary">Previous runs</span>
      {content}
    </div>
  );
};

export const CongaTestUploadPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [createBatch, { isLoading }] = useCreateCongaTestBatchMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const templateUrl = useMemo(() => URL.createObjectURL(new Blob([TEMPLATE_CSV], { type: 'text/csv' })), []);

  const handleChooseFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const result = await createBatch(formData).unwrap();
      navigate(`/internal/conga-test/${result.batchId}`);
    } catch (err) {
      toast.error(getApiMessage(err) ?? (err instanceof Error ? err.message : 'Upload failed'));
    }
  }, [createBatch, file, navigate]);

  return (
    <DsmPageContainer title="Conga Test Envelopes">
      <PageHeader title="Conga Test Envelopes" />
      <div className="mx-6 flex flex-col gap-16">
        <p className="text-sm text-text-secondary">
          Upload a CSV with one row per test envelope (max 100 rows). Each row starts from a complete South Carolina
          payload (Conga Payload Standard v6) and overrides only the columns present in the CSV; a column left out
          keeps the base value, while a blank contract cell empties that field. Each row is submitted to Conga in
          turn and its signing URLs appear on the next page.
        </p>
        <div className="flex flex-col gap-4 text-sm text-text-secondary">
          <span className="font-bold text-text-primary">Required columns</span>
          <span>ProgramType: {CONGA_PROGRAM_TYPES.join(', ')}</span>
          <span>FinanceType: {CONGA_FINANCE_TYPES.join(', ')}</span>
          <span>PrimaryApplicant.FirstName, PrimaryApplicant.LastName, PrimaryApplicant.Email</span>
          <span className="font-bold text-text-primary">Optional columns</span>
          <span>label: a name for the row on the results page</span>
          <span>
            Package: State (with StateName), Market, Language, LanguageWaiver, Name, QuoteId, VaultOnCompletion (with
            LoanId, LoanAmount, Lender). ExternalId and ContractId are generated as new GUIDs for every row.
          </span>
          <span>
            Signers: &lt;Role&gt;.FirstName, .LastName, .Email and .RedirectUrl for CoApplicant, LicensedInstaller,
            UtilityAccountHolder or Salesperson (first name, last name and email together)
          </span>
          <span>
            Contract: contract:&lt;Exact Field Name&gt; for any v6 contract field. Numbers and booleans are written
            as plain values (null to clear); contract:Equipment takes a JSON array. The base contract is an energy
            backup system, so a solar row should set the solar fields and clear the battery ones. The template has
            every column, with a complete solar lease row and a complete energy backup lease row.
          </span>
        </div>
        <a href={templateUrl} download={TEMPLATE_FILE_NAME} className="text-text-link">
          Download template CSV
        </a>
        <div className="flex items-center gap-8">
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            onClick={handleChooseFile}
            disabled={isLoading}
          >
            Choose file
          </PalmettoButton>
          <span className="text-sm text-text-secondary">{file?.name ?? 'No file chosen'}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="conga-csv-input"
            onChange={handleFileChange}
          />
        </div>
        <div>
          <PalmettoButton type="button" layout="hug" onClick={handleSubmit} disabled={!file || isLoading}>
            {isLoading ? 'Uploading…' : 'Submit'}
          </PalmettoButton>
        </div>
        <PreviousRuns />
      </div>
    </DsmPageContainer>
  );
};
